import PropTypes from 'prop-types';
import * as Yup from 'yup';
import React, { useContext, useEffect, useState } from 'react';
import { useFormik, Form, FormikProvider, FieldArray } from 'formik';
// material
import {
  Stack,
  TextField,
  Box,
  Card,
  CardHeader,
  IconButton,
  Fab,
  Button,
  Divider,
  Chip,
  Tab
} from '@mui/material';
import { TabContext, TabList, TabPanel, LoadingButton } from '@mui/lab';
import { grey } from '@mui/material/colors';
import { styled } from '@mui/material/styles';
// material
import { Icon } from '@iconify/react';
import plusFill from '@iconify/icons-eva/plus-fill';
import trashFill from '@iconify/icons-eva/trash-2-fill';
import checkmarkCircleFill from '@iconify/icons-eva/checkmark-circle-2-fill';
// contexts
import { useAuth } from '../../../contexts/Auth';
import { SettingsContext } from '../../../contexts/Settings';
import { EventContext } from '../../../contexts/Event';
// services
import { updateEvent } from '../../../services/Event';

const CardStyle = styled(Card)(({ theme }) => ({
  boxShadow: 'none',
  marginBottom: theme.spacing(2),
  border: `1px dashed ${grey[300]}`
}));

EventFormInfo.propTypes = {
  event: PropTypes.object,
  handleNext: PropTypes.func.isRequired,
  handleBack: PropTypes.func.isRequired,
  edit: PropTypes.bool
};

export default function EventFormInfo({ event, handleNext, handleBack, edit }) {
  const { authUser } = useAuth();
  const { authSettings } = useContext(SettingsContext);
  const { setEvent } = useContext(EventContext);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [eventInfoValues, setEventInfoValues] = useState({
    infos: [
      {
        title: '',
        description: '',
        translation: {
          pt: {
            title: '',
            description: ''
          }
        }
      }
    ]
  });

  useEffect(() => {
    if (event?.infos?.length) {
      setEventInfoValues({ infos: event.infos });
    }
  }, [event]);

  const EventInfoSchema = Yup.object().shape({
    infos: Yup.array().of(
      Yup.object().shape({
        title: Yup.string().required('Title is required'),
        description: Yup.string(),
        translation: Yup.object({
          pt: Yup.object({
            title: Yup.string(),
            description: Yup.string()
          })
        })
      })
    )
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: eventInfoValues,
    validationSchema: EventInfoSchema,
    onSubmit: async (values) => {
      setIsSubmitted(false);
      setIsLoading(true);

      try {
        const idToken = await authUser.getIdToken();
        const headers = {
          Authorization: `Bearer ${idToken}`,
          'Account-Key': authSettings.server.path
        };

        const payload = {
          infos: values.infos.filter((info) => info.title)
        };

        await updateEvent({ headers, payload, id: event._id });
        setEvent((event) => ({ ...event, ...payload }));

        setIsLoading(false);

        if (!edit) {
          handleNext();
        } else {
          setIsSubmitted(true);
        }
      } catch (err) {
        setIsLoading(false);
        console.error(err);
        alert('An error occurred! Please, try again.');
      }
    }
  });

  const { values, errors, touched, handleSubmit, getFieldProps } = formik;

  return (
    <Box>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <FieldArray
            name="infos"
            render={(fieldArrayHelpers) => (
              <>
                {values.infos.map((_, i) => (
                  <CardStyle key={i}>
                    <CardHeader
                      title={`Info ${i + 1}`}
                      action={
                        i > 0 && (
                          <IconButton
                            color="error"
                            aria-label="delete"
                            onClick={() => fieldArrayHelpers.remove(i)}
                          >
                            <Icon icon={trashFill} />
                          </IconButton>
                        )
                      }
                    />
                    <Stack spacing={2} sx={{ p: 2 }}>
                      <TextField
                        fullWidth
                        label="[EN] Title*"
                        {...getFieldProps(`infos.${i}.title`)}
                        error={Boolean(
                          touched.infos &&
                            touched.infos[i]?.title &&
                            errors.infos &&
                            errors.infos[i]?.title
                        )}
                        helperText={
                          touched.infos &&
                          touched.infos[i]?.title &&
                          errors.infos &&
                          errors.infos[i]?.title
                        }
                      />

                      <TextField
                        fullWidth
                        label="[EN] Description (optional)"
                        multiline
                        rows={4}
                        {...getFieldProps(`infos.${i}.description`)}
                        error={Boolean(
                          touched.infos &&
                            touched.infos[i]?.description &&
                            errors.infos &&
                            errors.infos[i]?.description
                        )}
                        helperText={
                          touched.infos &&
                          touched.infos[i]?.description &&
                          errors.infos &&
                          errors.infos[i]?.description
                        }
                      />
                    </Stack>

                    <Divider>
                      <Chip label="Translations" size="small" />
                    </Divider>

                    <Box sx={{ width: '100%', typography: 'body1', marginTop: '0 !important' }}>
                      <TabContext value="pt">
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                          <TabList>
                            <Tab label="PT" value="pt" />
                          </TabList>
                        </Box>
                        <TabPanel value="pt">
                          <Stack spacing={2}>
                            <TextField
                              fullWidth
                              label="[PT] Title"
                              variant="filled"
                              {...getFieldProps(`infos.${i}.translation.pt.title`)}
                              error={Boolean(
                                touched.infos &&
                                  touched.infos[i]?.translation?.pt?.title &&
                                  errors.infos &&
                                  errors.infos[i]?.translation?.pt?.title
                              )}
                              helperText={
                                touched.infos &&
                                touched.infos[i]?.translation?.pt?.title &&
                                errors.infos &&
                                errors.infos[i]?.translation?.pt?.title
                              }
                            />

                            <TextField
                              fullWidth
                              label="[PT] Description (optional)"
                              variant="filled"
                              multiline
                              rows={4}
                              {...getFieldProps(`infos.${i}.translation.pt.description`)}
                              error={Boolean(
                                touched.infos &&
                                  touched.infos[i]?.translation?.pt?.description &&
                                  errors.infos &&
                                  errors.infos[i]?.translation?.pt?.description
                              )}
                              helperText={
                                touched.infos &&
                                touched.infos[i]?.translation?.pt?.description &&
                                errors.infos &&
                                errors.infos[i]?.translation?.pt?.description
                              }
                            />
                          </Stack>
                        </TabPanel>
                      </TabContext>
                    </Box>
                  </CardStyle>
                ))}

                <Box sx={{ textAlign: 'center', mt: -3 }}>
                  <Fab
                    size="medium"
                    color="primary"
                    aria-label="add"
                    onClick={() => fieldArrayHelpers.push({ title: '', description: '' })}
                  >
                    <Icon icon={plusFill} width="24" />
                  </Fab>
                </Box>
              </>
            )}
          />

          <Box>
            {!edit && (
              <>
                <Button size="large" onClick={handleBack} sx={{ mr: 1 }}>
                  Back
                </Button>
                <Button size="large" variant="outlined" onClick={handleNext} sx={{ mr: 1 }}>
                  Skip
                </Button>
              </>
            )}
            <LoadingButton
              size="large"
              type="submit"
              variant="contained"
              loading={isLoading}
              endIcon={isSubmitted && <Icon icon={checkmarkCircleFill} />}
            >
              Save
            </LoadingButton>
          </Box>
        </Form>
      </FormikProvider>
    </Box>
  );
}
